import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Handshake, PartyPopper, Store, Trees, Users, LucideIcon } from "lucide-react";
import { PageTitle } from "@/components/SettingsPanel";
import { fmtMoney } from "@/components/EventCard";
import { api } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";

interface Dash {
  venues: Record<string, number>; bookings: Record<string, number>; open_cases: number; restricted_users: number; notification_failures: number;
  finance?: Record<string, Record<string, string>>;
  platform: Record<string, number>;
}

const Tile = ({ label, value, to, id, tone = "", note, Icon }: { label: string; value: React.ReactNode; to?: string; id: string; tone?: string; note?: string; Icon?: LucideIcon }) => {
  const body = (
    <div data-testid={`dash-${id}`} className={`rounded-3xl border bg-card p-5 h-full transition-[transform] hover:-translate-y-0.5 ${tone}`}>
      <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">{Icon && <Icon className="h-4 w-4 text-primary" />}{label}</p>
      <p className="font-display text-3xl font-black mt-1">{value}</p>
      {note && <p className="text-[11px] text-muted-foreground mt-0.5" data-testid={`dash-${id}-note`}>{note}</p>}
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
};

export default function AdminDashboard() {
  const { t } = usePrefs();
  const [d, setD] = useState<Dash | null>(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div data-testid="admin-dashboard-page" className="h-40" />;
  return (
    <div data-testid="admin-dashboard-page" className="space-y-8">
      <PageTitle title={t("b7.dashTitle")} sub={t("b7.dashSub")} />
      <section className="space-y-3">
        <h2 className="text-sm font-bold text-muted-foreground">{t("dp.title")}</h2>
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
          <Tile id="customers" Icon={Users} label={t("dp.customers")} value={d.platform.customers} to="/admin/accounts" note={d.platform.customers_week ? t("dp.week").replace("{n}", String(d.platform.customers_week)) : undefined} />
          <Tile id="partners" Icon={Handshake} label={t("dp.partners")} value={d.platform.partners} to="/admin/partners" note={t("dp.active").replace("{n}", String(d.venues.approved))} />
          <Tile id="places" Icon={Store} label={t("dp.places")} value={d.platform.places} to="/admin" />
          <Tile id="events" Icon={PartyPopper} label={t("dp.events")} value={d.platform.events_upcoming} to="/admin/events" />
          <Tile id="spots" Icon={Trees} label={t("dp.spots")} value={d.platform.spots} to="/admin/spots"
            note={d.platform.suggestions_pending ? t("dp.suggest").replace("{n}", String(d.platform.suggestions_pending)) : d.platform.spots_hidden ? t("dp.hidden").replace("{n}", String(d.platform.spots_hidden)) : undefined} />
        </div>
      </section>
      <h2 className="text-sm font-bold text-muted-foreground -mb-5">{t("dp.today")}</h2>
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <Tile id="venues-pending" label={t("b7.venuesPending")} value={d.venues.pending} to="/admin/partners" tone={d.venues.pending ? "border-amber-500/60" : ""} />
        <Tile id="venues-approved" label={t("b7.venuesApproved")} value={d.venues.approved} to="/admin/partners" />
        <Tile id="bk-pending" label={t("b7.bkPending")} value={d.bookings.pending} to="/admin/bookings" />
        <Tile id="bk-today" label={t("b7.bkToday")} value={d.bookings.today} to="/admin/bookings" />
        <Tile id="attended-today" label={t("b7.attendedToday")} value={d.bookings.attended_today} />
        <Tile id="noshow-today" label={t("b7.noShowToday")} value={d.bookings.no_show_today} />
        <Tile id="open-cases" label={t("b7.openCases")} value={d.open_cases} to="/admin/cases" tone={d.open_cases ? "border-primary/50" : ""} />
        <Tile id="notif-failures" label={t("b7.notifFailures")} value={d.notification_failures} tone={d.notification_failures ? "border-rose-500/60" : ""} />
      </div>
      {d.finance && (
        <div className="grid gap-3 md:grid-cols-2">
          {Object.entries(d.finance).map(([c, v]) => (
            <Link to="/admin/finance" key={c} data-testid={`dash-finance-${c}`} className="rounded-3xl bg-foreground text-background p-5 grid grid-cols-3 gap-2">
              {(["closing", "payments", "exemptions"] as const).map((k) => (
                <div key={k}>
                  <p className="text-xs opacity-70">{t(k === "closing" ? "b7.outstanding" : k === "payments" ? "b7.collected" : "b7.waived")} · {c}</p>
                  <p className="font-display text-lg font-black"><bdi>{fmtMoney({ amount: v[k], currency: c } as any)}</bdi></p>
                </div>
              ))}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
